import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { Board } from '../models/board.model';
import { AuthService } from './auth.service';
import { BoardApiService } from './board-api.service';
import { BoardStateService } from './board-state.service';

export class EditorSession {
  readonly board = signal<Board | null>(null);
  readonly status = signal<'loading' | 'idle' | 'saving' | 'saved' | 'error'>('loading');
  readonly error = signal('');
  readonly selectedId = signal<string | null>(null);
  readonly selected = computed(() =>
    this.board()?.messages.find((m) => m.id === this.selectedId()),
  );
  history: Board[] = [];
  revision = 0;
  savedRevision = 0;
  loadId?: string;
  timer?: ReturnType<typeof setTimeout>;
  request?: Subscription;
  writing = false;
  constructor(
    readonly owner: string,
    readonly key: string,
  ) {}
}

/** Sessions own their writes, so navigation cannot cancel a queued edit. Nothing is stored on disk. */
@Injectable({ providedIn: 'root' })
export class BoardEditorSessionService {
  private auth = inject(AuthService);
  private api = inject(BoardApiService);
  private cache = inject(BoardStateService);
  private sessions = new Map<string, EditorSession>();

  constructor() {
    effect(() => {
      const owner = this.auth.user()?.id;
      for (const [key, session] of this.sessions) {
        if (session.owner !== owner) {
          clearTimeout(session.timer);
          session.request?.unsubscribe();
          session.board.set(null);
          session.history = [];
          this.sessions.delete(key);
        }
      }
    });
  }

  open(id: string | null): EditorSession {
    const owner = this.auth.user()?.id || '';
    const key = id || `new:${crypto.randomUUID()}`;
    const existing = this.sessions.get(key);
    if (existing?.owner === owner) {
      if (existing.status() === 'saved') this.load(existing, id!);
      return existing;
    }
    const session = new EditorSession(owner, key);
    this.sessions.set(key, session);
    if (id) {
      this.load(session, id);
    } else {
      const now = new Date().toISOString();
      session.board.set({
        id: '',
        public_id: '',
        name: 'Untitled board',
        title: '',
        status: 'draft',
        config: { rows: 3, columns: 24, rotationInterval: 10 },
        soundEnabled: true,
        messages: [],
        viewCount: 0,
        createdAt: now,
        updatedAt: now,
      });
      session.status.set('idle');
      session.revision++;
      this.schedule(session, 0);
    }
    return session;
  }

  change(session: EditorSession, update: (board: Board) => Board, debounce = false): void {
    const before = session.board();
    if (!before || !this.owned(session)) return;
    const after = update(structuredClone(before));
    if (JSON.stringify(before) === JSON.stringify(after)) return;
    // A server hash cannot recover the previous secret; passcode changes are not undoable.
    if (before.passcode === after.passcode) session.history.push(structuredClone(before));
    if (session.history.length > 100) session.history.shift();
    session.board.set(after);
    session.revision++;
    this.schedule(session, debounce ? 600 : 0);
  }

  undo(session: EditorSession): void {
    const prior = session.history.pop();
    const current = session.board();
    if (!prior || !current || !this.owned(session)) return;
    // Creation identity is not an undoable setting.
    session.board.set({
      ...prior,
      id: current.id,
      public_id: current.public_id,
      passcode: current.passcode,
      hasPasscode: current.hasPasscode,
    });
    if (!prior.messages.some((m) => m.id === session.selectedId()))
      session.selectedId.set(prior.messages[0]?.id || null);
    session.revision++;
    this.schedule(session, 0);
  }

  retry(session: EditorSession): void {
    if (!session.board()) this.load(session, session.loadId || session.key);
    else this.schedule(session, 0);
  }

  private load(session: EditorSession, id: string): void {
    session.loadId = id;
    session.status.set('loading');
    session.board.set(null);
    session.history = [];
    session.error.set('');
    session.request = this.api.getBoard(id).subscribe({
      next: (board) => {
        if (!this.owned(session)) return;
        session.board.set(board);
        if (!board.messages.some((m) => m.id === session.selectedId()))
          session.selectedId.set(board.messages[0]?.id || null);
        session.status.set('saved');
      },
      error: () => {
        if (!this.owned(session)) return;
        session.status.set('error');
        session.error.set('Unable to load this board. Retry or return to Boards.');
      },
    });
  }

  private owned(session: EditorSession): boolean {
    return !!session.owner && session.owner === this.auth.user()?.id;
  }

  private schedule(session: EditorSession, delay: number): void {
    clearTimeout(session.timer);
    session.error.set('');
    session.status.set('saving');
    session.timer = setTimeout(() => {
      session.timer = undefined;
      this.write(session);
    }, delay);
  }

  private write(session: EditorSession): void {
    if (!this.owned(session) || session.writing) return;
    const snapshot = session.board();
    if (!snapshot) return;
    const passcode = snapshot.passcode;
    if (
      !snapshot.name.trim() ||
      (passcode && passcode !== '***' && (passcode.length < 4 || passcode.length > 50))
    ) {
      session.status.set('error');
      session.error.set(
        !snapshot.name.trim()
          ? 'Board name is required.'
          : 'Passcode must contain 4–50 characters.',
      );
      return;
    }
    const revision = session.revision;
    session.writing = true;
    session.status.set('saving');
    const request = snapshot.id
      ? this.api.updateBoard(snapshot.id, snapshot)
      : this.api.createBoard(snapshot);
    session.request = request.subscribe({
      next: (saved) => {
        session.writing = false;
        if (!this.owned(session)) return;
        session.savedRevision = revision;
        const current = session.board()!;
        // Only server identity is merged. A late response must never replace newer edits.
        session.board.set({
          ...current,
          id: saved.id,
          public_id: saved.public_id,
          hasPasscode: saved.hasPasscode,
          updatedAt: saved.updatedAt,
          passcode:
            current.passcode === snapshot.passcode
              ? saved.hasPasscode
                ? '***'
                : undefined
              : current.passcode,
        });
        session.history = session.history.map((board) => ({
          ...board,
          passcode: saved.hasPasscode ? '***' : undefined,
          hasPasscode: saved.hasPasscode,
        }));
        session.loadId = saved.id;
        this.sessions.set(saved.id, session);
        this.cache.acceptSaved(saved, session.owner);
        if (session.revision !== revision) {
          if (!session.timer) this.write(session);
        } else {
          session.status.set('saved');
          session.error.set('');
        }
      },
      error: () => {
        session.writing = false;
        if (!this.owned(session)) return;
        clearTimeout(session.timer);
        session.timer = undefined;
        session.status.set('error');
        session.error.set('Changes are still here. Check your connection and retry.');
      },
    });
  }
}
